import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import SessionBanner from "@/components/SessionBanner";
import FeedbackButton from "@/components/FeedbackButton";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import GroupPage from "@/pages/GroupPage";
import JoinPage from "@/pages/JoinPage";
import Onboarding from "@/pages/Onboarding";
import Help from "@/pages/Help";
import AdminFeedback from "@/pages/AdminFeedback";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sageGray">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sageGray">Loading…</div>;
  if (user) {
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    return <Navigate to={next && next.startsWith("/") ? next : "/dashboard"} replace />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/groups/:groupId" element={<Protected><GroupPage /></Protected>} />
            <Route path="/groups/:groupId/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/join/:code" element={<JoinPage />} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin/feedback" element={<Protected><AdminFeedback /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/billing/cancel" element={<Protected><BillingCancel /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <SessionBanner />
        <ProtectedFeedback />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

// Only render the floating feedback launcher when there's a logged-in user.
function ProtectedFeedback() {
  const { user } = useAuth();
  if (!user) return null;
  return <FeedbackButton />;
}
